<template>
  <el-dialog :model-value="modelValue" title="Yangi yil jadvali" width="560px" destroy-on-close
    @update:model-value="onVisibilityChange">
    <div v-loading="working" class="sbs-newyear">
      <p class="sbs-newyear__intro">
        Yil ochilgandan keyin unga <b>ustunlar biriktiriladi</b>, so'ng choraklar bo'yicha
        qiymatlar to'ldiriladi.
      </p>

      <div class="sbs-field">
        <label class="sbs-field__label">Yil <span class="sbs-req">*</span></label>
        <el-input-number v-model="year" :min="1900" :max="2200" controls-position="right"
          style="width: 160px" />
        <div v-if="yearExists" class="sbs-hint-row is-warn">
          Bu yil allaqachon ro'yxatda bor — uni yuqoridagi ro'yxatdan tanlang.
        </div>
      </div>

      <div class="sbs-field">
        <el-checkbox v-model="copySkeleton" :disabled="!sourceYears.length">
          Ustunlar skeletini boshqa yildan ko'chirish
        </el-checkbox>
        <div v-if="!sourceYears.length" class="sbs-hint-row">
          Hozircha yilga biriktirilgan ustun yo'q — ustunlarni yangi yilda qo'lda yaratasiz.
        </div>
      </div>

      <div v-if="copySkeleton && sourceYears.length" class="sbs-field sbs-indent">
        <label class="sbs-field__label">Manba yil</label>
        <el-select v-model="sourceYear" style="width: 160px">
          <el-option v-for="y in sourceYears" :key="y" :label="y" :value="y" />
        </el-select>
        <div class="sbs-hint-row">
          {{ sourceColumns.length }} ta ustun nusxalanadi (sarlavha, guruh, turi, miqyos va tartib
          saqlanadi). Qiymatlar ko'chmaydi — ular chorakma-chorak kiritiladi.
        </div>
      </div>

      <div class="sbs-newyear__note">
        Yil ro'yxatda doimiy qolishi uchun unga kamida bitta ustun biriktirilishi yoki qiymat
        kiritilishi kerak — server bo'sh yilni saqlamaydi.
      </div>
    </div>

    <template #footer>
      <el-button @click="onVisibilityChange(false)">Bekor qilish</el-button>
      <el-button type="primary" :loading="working" :disabled="yearExists || !year" @click="onCreate">
        {{ copySkeleton && sourceColumns.length ? "Yaratish va ustunlarni ko'chirish" : "Yaratish" }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage, ElNotification } from "element-plus";
import { API_BASE, displayLangText, yearsFromColumns } from "./subsidiesTypes";

export default {
  name: "SubsidiesNewYearDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    years: { type: Array, default: () => [] },
    // Barcha ustunlar (yil filtrisiz) — skelet manbasi shu ro'yxatdan olinadi.
    columns: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "created"],
  data() {
    return {
      year: null,
      copySkeleton: true,
      sourceYear: null,
      working: false,
    };
  },
  computed: {
    yearExists() {
      return this.years.includes(Number(this.year));
    },
    sourceYears() {
      return yearsFromColumns(this.columns).sort((a, b) => b - a);
    },
    sourceColumns() {
      return this.columns.filter((c) => Number(c.year) === Number(this.sourceYear));
    },
  },
  watch: {
    modelValue(val) {
      if (!val) return;
      const latest = this.years[0] || new Date().getFullYear();
      this.year = latest + 1;
      this.sourceYear = this.sourceYears[0] || null;
      this.copySkeleton = this.sourceYears.length > 0;
      this.working = false;
    },
  },
  methods: {
    onVisibilityChange(val) {
      this.$emit("update:modelValue", val);
    },
    onCreate() {
      const target = Number(this.year);
      if (!target || this.yearExists) return;
      if (!this.copySkeleton || !this.sourceColumns.length) {
        ElMessage({ type: "success", message: `${target}-yil ochildi — endi ustunlarni biriktiring` });
        this.$emit("created", { year: target, copied: 0 });
        this.$emit("update:modelValue", false);
        return;
      }
      this.working = true;
      // `copy-period` faqat hujayralarni ko'chiradi va ustunni `column_id` bo'yicha
      // qidiradi — yilga biriktirilgan ustun boshqa yilda mavjud emas. Shuning uchun
      // skelet = manba ustunlarning yangi yil uchun nusxasini YARATISH.
      const payloads = this.sourceColumns.map((c) => ({
        group_code: c.group_code || "",
        group: c.group,
        title: c.title,
        unit: c.unit,
        value_type: c.value_type,
        scale: c.scale,
        decimals: c.decimals,
        year: target,
        order: c.order,
        is_active: c.is_active !== false,
      }));
      this.createSequential(payloads, 0, target, 0);
    },
    // Ketma-ket yuboriladi: server `order` ni o'zgartirmaydi, lekin xato bo'lsa
    // qaysi ustunda to'xtaganini aniq ko'rsatish kerak.
    createSequential(payloads, idx, target, done) {
      if (idx >= payloads.length) {
        this.working = false;
        ElMessage({ type: "success", message: `${target}-yil ochildi, ${done} ta ustun ko'chirildi` });
        this.$emit("created", { year: target, copied: done });
        this.$emit("update:modelValue", false);
        return;
      }
      this.$http
        .post(`${API_BASE}/columns`, payloads[idx])
        .then(() => this.createSequential(payloads, idx + 1, target, done + 1))
        .catch((error) => {
          this.working = false;
          ElNotification({
            title: "Ustunni ko'chirishda xatolik",
            message: `"${displayLangText(payloads[idx].title)}": ` +
              (error?.response?.data?.message || "Iltimos, qayta urinib ko'ring.") +
              (done ? ` (${done} ta ustun ko'chirilgan edi)` : ""),
            type: "error",
            duration: 8000,
          });
          if (done) this.$emit("created", { year: target, copied: done });
        });
    },
  },
};
</script>

<style scoped lang="scss">
$muted: #8a94a6;

.sbs-newyear__intro {
  margin: 0 0 18px;
  font-size: 12.5px;
  color: $muted;
}

.sbs-field {
  margin-bottom: 18px;

  &__label {
    display: block;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #303133;
  }
}

.sbs-indent {
  padding-left: 24px;
  border-left: 2px solid #ebeef5;
}

.sbs-req {
  color: #e11d48;
}

.sbs-hint-row {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: $muted;

  &.is-warn {
    color: #92620a;
  }
}

.sbs-newyear__note {
  padding: 8px 12px;
  background: #f6f9fc;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.55;
  color: $muted;
}
</style>
